import { Select, Loader, Group, Text, Box } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import type { FlowVersionSummaryViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { translateFlowType } from "@utils/flowTypeTranslations";
import { versionKey } from "../VersionHistory";
import "./VersionSelector.scss";

interface VersionSelectorProps {
    versions: FlowVersionSummaryViewModel[];
    selectedVersion: FlowVersionSummaryViewModel | null;
    onVersionChange: (version: FlowVersionSummaryViewModel) => void;
    isLoading?: boolean;
}

export const VersionSelector = ({
    versions,
    selectedVersion,
    onVersionChange,
    isLoading = false,
}: VersionSelectorProps) => {
    const { t } = useTranslation();
    const byKey = new Map(versions.map((version) => [versionKey(version), version]));

    const data = versions.map((version) => ({
        value: versionKey(version),
        label: [
            t("admin.flowConfigs.versions.label", { version: version.version }),
            translateFlowType(version.flowType, t),
            version.isActive ? t("admin.flowConfigs.status.live") : null,
        ].filter(Boolean).join(" · "),
    }));

    return (
        <Select
            className="version-selector"
            aria-label={t("admin.flowConfigs.versions.select")}
            value={selectedVersion ? versionKey(selectedVersion) : null}
            onChange={(value) => {
                const version = value ? byKey.get(value) : undefined;
                if (version) onVersionChange(version);
            }}
            data={data}
            size="sm"
            allowDeselect={false}
            disabled={isLoading}
            rightSection={isLoading ? <Loader size={16} /> : undefined}
            comboboxProps={{ withinPortal: false }}
            renderOption={({ option }) => {
                const version = byKey.get(option.value);
                if (!version) return option.label;
                return (
                    <Group gap="sm" wrap="nowrap" className="version-selector__item">
                        <Text size="sm" fw={600} w={36}>{t("admin.flowConfigs.versions.label", { version: version.version })}</Text>
                        <Box miw={0} style={{ flex: 1 }}>
                            <Text size="sm" truncate>{translateFlowType(version.flowType, t)}</Text>
                            <Text size="xs" c="dimmed">{version.createdAt?.toLocaleString() ?? "-"}</Text>
                        </Box>
                        {version.isActive && <Badge size="xs" color="green">{t("admin.flowConfigs.status.live")}</Badge>}
                    </Group>
                );
            }}
        />
    );
};
